/**
 * Sheet „Konditionen ändern" (Spec 04a, „Sheet Konditionen ändern") — die
 * Ablösung der 03er-Regel, sobald ein Vertrag eine Sollstellung trägt.
 * Erscheint im Reiter *Stammdaten* eines Vertrags, ausgelöst über
 * `contract-terms-add` in `ContractMasterData`.
 *
 * „Gültig ab" ist eine **Monatsauswahl, kein freies Datum** (AK-295):
 * `<input type="month">` ist dafür der native Weg — der Browser lässt keinen
 * Tag zu, ohne dass der Client selbst etwas verbieten müsste.
 *
 * Der Verlauf (`contract-terms-history`) ist rein darstellend und kommt aus
 * `ContractDetail.terms[]` — keine eigene Abfrage, keine Fachlogik: Zeitraum,
 * Beträge und Grund stehen fertig im übergebenen `terms`-Array, nur die
 * Zeitraum-**Formatierung** (Tag.Monat.Jahr aus `valid_from`/`valid_to`)
 * kommt aus `lib/date.ts` — dasselbe Prinzip wie `lib/money.ts`s
 * Cent-zu-Euro-Umrechnung, keine Wortbildung wie ein Monatsname.
 */

import { useState } from 'react'
import type { SubmitEvent } from 'react'

import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { ApiError } from '@/lib/api'
import { formatDate } from '@/lib/date'
import { centsToDecimalInput, parseCents } from '@/lib/money'
import { useAddContractTerms } from '@/features/contracts/api'
import type { ContractTermsCreate, ContractTermSummary, TermReason } from '@/features/contracts/api'
import { FormField, FIELD_CLASS } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'
import { TermReasonField } from '@/features/contracts/TermReasonField'

interface ContractTermsSheetProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly contractId: string
  readonly terms: readonly ContractTermSummary[]
}

const EMPTY_FORM = { validFrom: '', rent: '', advance: '', reason: '', note: '' }
type FormValues = typeof EMPTY_FORM

/** Vorbelegung aus dem aktuell offenen Satz (`valid_to === null`) — eine
 * Mieterhöhung ändert meist nur die Miete, die Nebenkosten bleiben gleich.
 * Dieselbe Art „sinnvolle Vorbelegung" wie beim Duplizieren (`UnitSheet`,
 * Spec 02b) — keine Fachlogik, nur ein Ausgangswert zum Weiterbearbeiten. */
function deriveFormFromOpenTerm(terms: readonly ContractTermSummary[]): FormValues {
  const openTerm = terms.find((term) => term.valid_to === null)
  if (openTerm === undefined) return EMPTY_FORM
  return {
    validFrom: '',
    rent: centsToDecimalInput(openTerm.base_rent_net_cents),
    advance: centsToDecimalInput(openTerm.operating_cost_advance_net_cents),
    reason: '',
    note: '',
  }
}

export function ContractTermsSheet({ open, onOpenChange, contractId, terms }: ContractTermsSheetProps) {
  const addTermsMutation = useAddContractTerms()

  const [form, setForm] = useState<FormValues>(EMPTY_FORM)
  const [fieldError, setFieldError] = useState<FieldError | undefined>(undefined)
  const [generalError, setGeneralError] = useState<string | undefined>(undefined)

  // Jedes Öffnen beginnt frisch — dasselbe Muster wie `UnitSheet`/`MoveSheet`
  // (React „Adjusting state when a prop changes", während des Renderns).
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setForm(deriveFormFromOpenTerm(terms))
      setFieldError(undefined)
      setGeneralError(undefined)
    }
  }

  function updateField(name: keyof FormValues) {
    return (value: string) => {
      setForm((current) => ({ ...current, [name]: value }))
    }
  }

  const parsedRent = parseCents(form.rent)
  const isValid = form.validFrom !== '' && parsedRent !== null && form.reason !== ''

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    // `isValid` bindet `parsedRent !== null` bereits ein — TS verfolgt das
    // über die Bedingung (aliased condition narrowing), ein zweiter Vergleich
    // wäre unerreichbarer Code (dasselbe Muster wie `ContractMasterData.tsx`).
    if (!isValid) return
    setFieldError(undefined)
    setGeneralError(undefined)

    const noteTrimmed = form.note.trim()
    const body: ContractTermsCreate = {
      valid_from: `${form.validFrom}-01`,
      base_rent_net_cents: parsedRent,
      operating_cost_advance_net_cents: parseCents(form.advance) ?? 0,
      // Umsatzsteuer erscheint in keinem Bildschirm dieser Spec (ADR-Vorgabe
      // „Kein USt-Feld, keine Netto/Brutto-Spalte, kein Satz im Formular") —
      // dieselbe 0 wie beim ersten Konditionssatz eines neu angelegten
      // Vertrags, den `POST /contracts` ohne UI-Eingabe setzt.
      tax_rate_bp: 0,
      reason: form.reason as TermReason,
      ...(noteTrimmed !== '' ? { note: noteTrimmed } : {}),
    }

    try {
      await addTermsMutation.mutateAsync({ contractId, body })
      onOpenChange(false)
    } catch (error) {
      if (error instanceof ApiError && error.field !== null) {
        setFieldError({ field: error.field, message: error.message })
      } else if (error instanceof ApiError) {
        setGeneralError(error.message)
      } else {
        setGeneralError('Die Verbindung zum Server ist unterbrochen.')
      }
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        className="data-[side=right]:!w-full data-[side=right]:sm:!max-w-none md:data-[side=right]:!w-[480px] md:data-[side=right]:!max-w-[480px]"
      >
        <form
          data-testid="contract-terms-sheet"
          onSubmit={(event) => void handleSubmit(event)}
          className="flex h-full flex-col"
        >
          <SheetHeader>
            <SheetTitle className="text-section font-(--font-weight-section) text-text">
              Konditionen ändern
            </SheetTitle>
          </SheetHeader>

          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
            <FormField
              label="Gültig ab"
              testId="contract-terms-valid-from"
              apiField="valid_from"
              errorPrefix="contract-terms"
              type="month"
              required
              value={form.validFrom}
              onChange={updateField('validFrom')}
              error={fieldError}
            />
            <FormField
              label="Miete"
              testId="contract-terms-rent"
              apiField="base_rent_net_cents"
              errorPrefix="contract-terms"
              inputMode="decimal"
              required
              value={form.rent}
              onChange={updateField('rent')}
              error={fieldError}
            />
            <FormField
              label="Nebenkostenvorauszahlung"
              testId="contract-terms-advance"
              apiField="operating_cost_advance_net_cents"
              errorPrefix="contract-terms"
              inputMode="decimal"
              value={form.advance}
              onChange={updateField('advance')}
              error={fieldError}
            />
            <TermReasonField
              testId="contract-terms-reason"
              errorPrefix="contract-terms"
              value={form.reason}
              onChange={updateField('reason')}
              error={fieldError}
            />
            <FormField
              label="Notiz"
              testId="contract-terms-note"
              apiField="note"
              errorPrefix="contract-terms"
              value={form.note}
              onChange={updateField('note')}
              error={fieldError}
            />

            <p className="text-label text-muted">
              Konditionen gelten ab einem Monatsersten. Bereits erzeugte Sollstellungen bleiben unverändert.
            </p>

            {terms.length > 0 && (
              <div data-testid="contract-terms-history" className="border-border flex flex-col gap-2 border-t pt-3">
                <span className="text-label text-muted">Verlauf</span>
                <ul className="flex flex-col gap-2">
                  {terms.map((term) => (
                    <li
                      key={term.id}
                      data-testid={`contract-terms-row-${term.id}`}
                      className="border-border flex flex-col gap-0.5 rounded-card border px-3 py-2"
                    >
                      <span className="text-body text-text">
                        {term.valid_to === null
                          ? `seit ${formatDate(term.valid_from)}`
                          : `${formatDate(term.valid_from)} – ${formatDate(term.valid_to)}`}
                      </span>
                      <span className="text-label text-muted">
                        {term.base_rent_display} · {term.operating_cost_advance_display} · {term.reason.label}
                      </span>
                      {term.note !== null && term.note !== '' && (
                        <span className="text-label text-muted">{term.note}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <SheetFooter>
            {generalError !== undefined && (
              <p data-testid="contract-terms-error" role="alert" className="text-label text-status-overdue">
                {generalError}
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                data-testid="contract-terms-cancel"
                onClick={() => {
                  onOpenChange(false)
                }}
                className={`text-text px-4 ${FIELD_CLASS}`}
              >
                Abbrechen
              </button>
              <button
                type="submit"
                data-testid="contract-terms-save"
                disabled={!isValid || addTermsMutation.isPending}
                aria-busy={addTermsMutation.isPending}
                className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out) disabled:opacity-60"
              >
                Konditionen ändern
              </button>
            </div>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
